import { applyMiddleware, compose, createStore, Middleware } from 'redux'
import createSagaMiddleware, { EffectMiddleware } from 'redux-saga'
import type { Action } from '~/actions'
import rootReducer from '~/reducers'
import SagaManager from '~/sagas'
import type { State } from '~/types'

// Create dummy which will be replaced in configureStore
const actionInterceptor: EffectMiddleware = next => effect => next(effect)
const effectMiddleware = (next: any) => actionInterceptor(next)

const sagaMiddleware = createSagaMiddleware({ effectMiddlewares: [effectMiddleware] })

const optimisticUpdateHelper: Middleware = store => next => (action: Action) => {
	if (action['@CONTEXT']?.optimistic) {
		return next({
			...action,
			'@CONTEXT': {
				...action['@CONTEXT'],
				prevState: store.getState().cart
			}
		})
	} else {
		return next(action)
	}
}

const middlewares = [optimisticUpdateHelper, sagaMiddleware]

const composeEnhancers: typeof compose = window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ || compose

export default function configureStore(initialState?: Partial<State>) {
	const store = createStore(rootReducer(), initialState || {}, composeEnhancers(applyMiddleware(...middlewares)))

	// run sagas
	SagaManager.startSagas(sagaMiddleware)

	if (import.meta.env.DEV && import.meta.hot) {
		// Hot reload reducers (requires Webpack or Browserify HMR to be enabled)
		import.meta.hot.accept('~/reducers', async () => {
			const reducers = await import('~/reducers')
			store.replaceReducer(reducers.default as any)
		})

		import.meta.hot.accept('~/sagas', async () => {
			const newSagaManager = await import('~/sagas')
			SagaManager.cancelSagas(store as any)
			newSagaManager.default.startSagas(sagaMiddleware)
		})
	}

	return store
}
